import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useCashbookStatus } from "../../../../helpers/cashbookStatus";
import { getSpendsCheck, listLedgerHeads, saveExpense, saveSpendsTotal, verifyEntries } from "../../../../services/cashBookApi";
import { useKT } from "../../i18n/kt";
import { kp } from "../../karkhanaPaths";
import { newRequestId } from "../../rounds/entryQueue";
import { fmtDay, rupees } from "../../rates/rateModel";
import PageHeader from "../../ui/PageHeader";
import CashbookGuard, { readRole } from "../CashbookGuard";
import { fmtTime, num } from "../money";
import { headLabel } from "../supervisor/SpendForm";
import type { EntryView, LedgerHead, Override, SpendsCheck as SpendsCheckData } from "../types";
import { isOwnerRole, ownerRefusal } from "./ownerText";
import PhotoBand from "./PhotoBand";
import { gapCleared, GROUP_MAX } from "./sheetCheckModel";
import "../cashbook.scss";
import "./owner.scss";

const cleanMoney = (s: string) => s.replace(/[^\d]/g, "").slice(0, 7);

/** Spends a "Verify these n" may take: unverified, active, not own, no tax badge; only while the page total matches. */
export const spendGroup = (c: SpendsCheckData): EntryView[] =>
  gapCleared({ paperTotal: c.pageTotal, gap: c.gap, totalOverridden: c.totalOverridden })
    ? c.entries.filter((e) => e.status === "ACTIVE" && e.verifyState !== "VERIFIED" && e.canVerify === "OK" && !e.section40A && !e.pendingChange && e.editCount === 0).slice(0, GROUP_MAX)
    : [];

/** "On the paper but not typed yet → Add from photo": a spend typed by staff from this page (recorder = holder, 7.8). */
const AddFromPhoto = ({ check, photoId, onAdded }: { check: SpendsCheckData; photoId: string; onAdded: () => void }) => {
  const { t, lang } = useKT();
  const [open, setOpen] = useState(false);
  const [heads, setHeads] = useState<LedgerHead[] | null>(null);
  const [amount, setAmount] = useState("");
  const [headId, setHeadId] = useState("");
  const [paidTo, setPaidTo] = useState("");
  const [note, setNote] = useState("");
  const [requestId, setRequestId] = useState(newRequestId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || heads) return;
    listLedgerHeads(false)
      .then((res) => setHeads(res?.success ? res.data?.heads || [] : []))
      .catch(() => setError(t("Couldn't load. Check the network.")));
  }, [open, heads]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!amount || !headId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await saveExpense({
        amount,
        ledgerHeadId: headId,
        paidTo: paidTo.trim() || null,
        note: note.trim() || null,
        date: check.date,
        billPhotoId: null,
        spendsPhotoId: photoId,
        holderUserId: check.holder.userId,
        clientRequestId: requestId,
      });
      if (!res?.success) {
        setError(ownerRefusal(res, t));
        return;
      }
      setAmount("");
      setPaidTo("");
      setNote("");
      setRequestId(newRequestId());
      onAdded();
    } catch {
      setError(t("Not saved. Check the network and retry."));
    } finally {
      setBusy(false);
    }
  };

  if (!open)
    return (
      <div className="cbo-add">
        <p className="cb-muted">{t("On the paper but not typed yet.")}</p>
        <button type="button" className="kh-btn" onClick={() => setOpen(true)}>
          {t("Add from photo")}
        </button>
      </div>
    );
  return (
    <div className="cbo-add">
      <h3 className="cb-section">{t("Add from photo")}</h3>
      <div className="cbo-form">
        <label className="cb-field">
          <span className="cb-field__label">{t("Amount")}</span>
          <input inputMode="numeric" value={amount} onChange={(e) => setAmount(cleanMoney(e.target.value))} autoFocus />
        </label>
        <label className="cb-field">
          <span className="cb-field__label">{t("Spend type")}</span>
          <select value={headId} onChange={(e) => setHeadId(e.target.value)}>
            <option value="">{heads ? t("Choose…") : t("Loading…")}</option>
            {(heads || []).map((h) => (
              <option key={h.id} value={h.id}>
                {headLabel(h, lang)}
              </option>
            ))}
          </select>
        </label>
        <label className="cb-field">
          <span className="cb-field__label">{t("Paid to")}</span>
          <input value={paidTo} onChange={(e) => setPaidTo(e.target.value)} maxLength={80} />
        </label>
      </div>
      <label className="cb-field">
        <span className="cb-field__label">{t("For what work? (optional)")}</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
      </label>
      <div className="cbo-decide__btns">
        <button type="button" className="kh-btn is-primary" disabled={busy || !amount || !headId} onClick={save}>
          {busy ? t("Saving…") : t("Save {{amount}} spend", { amount: rupees(amount || 0) })}
        </button>
        <button type="button" className="kh-btn" onClick={() => setOpen(false)}>
          {t("Back")}
        </button>
      </div>
      {error ? (
        <p className="cbo-row__err" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};

const SpendsScreen = () => {
  const { t } = useKT();
  const { photoId } = useParams<{ photoId: string }>();
  const status = useCashbookStatus();
  const owner = isOwnerRole(readRole());
  const [check, setCheck] = useState<SpendsCheckData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [total, setTotal] = useState("");
  const [retyping, setRetyping] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<Record<string, string>>({});
  const [override, setOverride] = useState<null | { kind: "total" } | { kind: "entry"; entry: EntryView }>(null);
  const [reason, setReason] = useState("");
  const [shown, setShown] = useState(0);
  const [loadedAt] = useState(Date.now);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await getSpendsCheck(photoId);
      if (res?.success && res.data) setCheck(res.data);
      else setLoadError(ownerRefusal(res, t));
    } catch {
      setLoadError(t("Couldn't load. Check the network."));
    }
  }, [photoId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const header = (
    <PageHeader
      title={check ? t("Spends page · {{name}} · {{date}} · {{n}} lines", { name: check.holder.name, date: fmtDay(check.date), n: check.entries.length }) : t("Spends check")}
      back={kp.cashBook()}
      backLabel={t("Back")}
    />
  );

  if (loadError && !check)
    return (
      <div className="cb cbo">
        {header}
        <div className="cb-error" role="alert">
          <span>{loadError}</span>
          <button type="button" className="kh-btn" onClick={load}>
            {t("Retry")}
          </button>
        </div>
      </div>
    );
  if (!check)
    return (
      <div className="cb cbo" role="status" aria-label={t("Loading…")}>
        {header}
        <div className="cb-skeleton is-big" />
        <div className="cb-skeleton" />
      </div>
    );

  const photos = [...check.photos].sort((a, b) => (a.uploadedAt < b.uploadedAt ? -1 : 1));
  const photo = photos[Math.min(shown, photos.length - 1)] || null;
  const needTotal = check.pageTotal === null || check.pageTotal === undefined || retyping;
  const cleared = gapCleared({ paperTotal: check.pageTotal, gap: check.gap, totalOverridden: check.totalOverridden });
  const group = spendGroup(check);
  const totalsDiffer = check.declaredTotal !== null && check.pageTotal !== null && num(check.declaredTotal) !== num(check.pageTotal);

  const saveTotal = async (ov: Override) => {
    const pageTotal = ov ? check.pageTotal || total : total;
    if (!pageTotal) return;
    setBusy("total");
    setError(null);
    try {
      const res = await saveSpendsTotal(photoId, { pageTotal, override: ov });
      if (!res?.success || !res.data) {
        setError(ownerRefusal(res, t));
        return;
      }
      setCheck(res.data);
      setRetyping(false);
      setOverride(null);
      setReason("");
      status.refresh();
    } catch {
      setError(t("Not saved. Check the network and retry."));
    } finally {
      setBusy(null);
    }
  };

  const verify = async (entries: EntryView[], ov: Override, key: string) => {
    setBusy(key);
    setError(null);
    setRowErr({});
    try {
      const res = await verifyEntries({
        batchId: newRequestId(),
        items: entries.map((e) => ({ recordType: e.recordType, id: e.id, version: e.version, photoSha256: photo?.sha256 || null, onScreenMs: Date.now() - loadedAt })),
        override: ov,
      });
      if (!res?.success || !res.data) {
        setError(ownerRefusal(res, t));
        return;
      }
      const failed = res.data.results.filter((r) => !r.ok);
      setRowErr(Object.fromEntries(failed.map((f) => [f.id, ownerRefusal(f, t)])));
      const ok = res.data.results.filter((r) => r.ok && r.entry).map((r) => r.entry as EntryView);
      setCheck((c) => (c ? { ...c, entries: c.entries.map((e) => ok.find((x) => x.id === e.id) || e) } : c));
      status.refresh();
    } catch {
      setError(t("Not saved. Check the network and retry."));
    } finally {
      setBusy(null);
    }
  };

  const photoPanel = (
    <aside className="cbo-check__photo" aria-label={t("Photo")}>
      <div className="cbo-photo__head">
        {photo ? <span title={photo.sha256}>{t("Photo {{time}} · not edited", { time: fmtTime(photo.uploadedAt) })}{photo.blurryAccepted ? ` · ${t("Blurry")}` : ""}</span> : <span>{t("No photo")}</span>}
        {photos.length > 1 ? (
          <span className="cbo-pager">
            <button type="button" className="kh-btn" disabled={shown <= 0} onClick={() => setShown(shown - 1)} aria-label={t("Previous page")}>
              ‹
            </button>
            <span>{t("Photo {{n}} of {{total}}", { n: shown + 1, total: photos.length })}</span>
            <button type="button" className="kh-btn" disabled={shown >= photos.length - 1} onClick={() => setShown(shown + 1)} aria-label={t("Next page")}>
              ›
            </button>
          </span>
        ) : null}
      </div>
      <PhotoBand photoId={photo?.id || null} band={null} alt={t("Spends page of {{name}}", { name: check.holder.name })} />
    </aside>
  );

  return (
    <div className="cb cbo">
      {header}
      <div className="cbo-check">
        {photoPanel}
        <section className="cbo-check__work">
          {needTotal ? (
            <form
              className="cbo-total"
              onSubmit={(e) => {
                e.preventDefault();
                saveTotal(null);
              }}
            >
              <label className="cb-field" htmlFor="cbo-spends-total">
                <span className="cbo-total__q">{t("Type the total written on the page")}</span>
                <span className="cbo-money">
                  <span aria-hidden="true">₹</span>
                  <input id="cbo-spends-total" inputMode="numeric" autoComplete="off" autoFocus value={total} onChange={(e) => setTotal(cleanMoney(e.target.value))} />
                </span>
              </label>
              <button type="submit" className="cb-btn is-primary" disabled={!total || busy === "total"}>
                {busy === "total" ? t("Saving…") : t("Next")}
              </button>
              {error ? (
                <p className="cb-error" role="alert">
                  {error}
                </p>
              ) : null}
            </form>
          ) : (
            <>
              <div className="cbo-totals" aria-live="polite">
                <p className="cbo-totals__line">
                  {t("Page total {{amount}}", { amount: rupees(check.pageTotal || 0) })} · {t("typed {{amount}}", { amount: rupees(check.typedTotal) })}
                  {check.declaredTotal !== null ? ` · ${t("Supervisor wrote {{amount}}", { amount: rupees(check.declaredTotal) })}` : ""}
                  {totalsDiffer ? <span className="cbo-chip is-doubt">{t("Totals differ")}</span> : null}{" "}
                  <button type="button" className="cb-link" onClick={() => { setTotal(check.pageTotal || ""); setRetyping(true); }}>
                    {t("Change")}
                  </button>
                </p>
                {check.totalOverridden ? (
                  <div className="cb-card is-warn">
                    <p>{t("You said the page's sum is wrong. Check each spend against the photo.")}</p>
                  </div>
                ) : cleared ? (
                  <div className="cb-card is-ok">
                    <p>{t("Page total matches the typed spends.")}</p>
                  </div>
                ) : (
                  <div className="cb-card is-bad" role="alert">
                    <p>
                      {t("{{gap}} unexplained. The typed spends add up to {{typed}}, but the page says {{paper}}.", {
                        gap: rupees(Math.abs(num(check.gap))),
                        typed: rupees(check.typedTotal),
                        paper: rupees(check.pageTotal || 0),
                      })}
                    </p>
                    <button type="button" className="cb-link" onClick={() => { setReason(""); setOverride({ kind: "total" }); }}>
                      {t("The page's sum is wrong")}
                    </button>
                  </div>
                )}
              </div>

              {error ? (
                <div className="cb-error" role="alert">
                  <span>{error}</span>
                </div>
              ) : null}

              <ul className="cbo-rows" style={{ marginTop: 13 }}>
                {check.entries.map((e) => {
                  const verified = e.verifyState === "VERIFIED";
                  const own = e.canVerify !== "OK";
                  const voided = e.status !== "ACTIVE";
                  return (
                    <li key={e.id} className={`cbo-row${verified ? " is-done" : ""}`}>
                      <div className="cbo-row__head">
                        <strong className="cbo-row__name">{rupees(e.amount)}</strong>
                        <span>{e.headName || t("Spend")}</span>
                        {e.paidTo ? <span className="cbo-row__code">{t("to {{name}}", { name: e.paidTo })}</span> : null}
                        {voided ? <span className="cbo-chip">{t("Removed")}</span> : null}
                      </div>
                      {e.note ? <p className="cb-muted" style={{ margin: "3px 0 0" }}>{e.note}</p> : null}
                      {e.section40A ? (
                        <p className="cbo-row__own" style={{ color: "var(--cb-amber)" }}>
                          {t("{{amount}} cash to one payee today. Over ₹10,000 in cash may not count as a tax expense.", { amount: rupees(e.section40A.dayTotal) })}
                        </p>
                      ) : null}
                      {e.pendingChange ? <span className="cbo-chip is-doubt">{t("change pending")}</span> : null}
                      {e.editCount > 0 ? <span className="cbo-chip is-doubt">{e.firstAmount ? t("Edited {{n}}×, first {{amount}}", { n: e.editCount, amount: rupees(e.firstAmount) }) : t("Edited {{n}}×", { n: e.editCount })}</span> : null}
                      {verified ? (
                        <div className="cbo-row__done">
                          <span className="cb-state is-ok">
                            {e.selfVerified ? t("Verified by {{name}} (self-verified)", { name: e.verifiedBy?.name || "" }) : t("Verified by {{name}}", { name: e.verifiedBy?.name || "" })}
                          </span>
                        </div>
                      ) : voided ? null : (
                        <div className="cbo-row__actions">
                          {own ? (
                            <>
                              <span className="cbo-chip is-own">{t(e.canVerify === "YOUR_OWN_PAY" ? "Your own pay" : "You recorded this")}</span>
                              {owner && e.canVerify !== "NOT_ALLOWED" ? (
                                <button type="button" className="kh-btn" disabled={!!busy} onClick={() => { setReason(""); setOverride({ kind: "entry", entry: e }); }}>
                                  {t("Verify anyway")}
                                </button>
                              ) : null}
                            </>
                          ) : (
                            <button type="button" className="kh-btn is-primary" disabled={!!busy || !photo} onClick={() => verify([e], null, e.id)}>
                              {busy === e.id ? t("Saving…") : t("Verify")}
                            </button>
                          )}
                          {e.recordedAt ? <span className="cb-muted">{fmtTime(e.recordedAt)}</span> : null}
                        </div>
                      )}
                      {rowErr[e.id] ? (
                        <p className="cbo-row__err" role="alert">
                          {rowErr[e.id]}
                        </p>
                      ) : null}
                    </li>
                  );
                })}
              </ul>

              <div className="cbo-group" style={{ marginTop: 13 }}>
                <button type="button" className="cb-btn is-primary" disabled={!group.length || !!busy} onClick={() => verify(group, null, "group")}>
                  {busy === "group" ? t("Saving…") : t("Verify these {{n}}", { n: group.length })}
                </button>
                {!cleared ? <p className="cb-muted">{t("Explain {{gap}} first", { gap: rupees(Math.abs(num(check.gap))) })}</p> : null}
              </div>

              <AddFromPhoto check={check} photoId={photoId} onAdded={load} />
            </>
          )}
        </section>
      </div>

      {override ? (
        <div className="cbo-dialog" role="dialog" aria-modal="true" aria-labelledby="cbo-spends-dialog">
          <div className="cbo-dialog__box">
            <h3 id="cbo-spends-dialog">{override.kind === "total" ? t("The page's sum is wrong") : t('Verify your own entry? It stays marked "self-verified" in the exceptions report.')}</h3>
            <label className="cb-field">
              <span className="cb-field__label">{t("Why?")}</span>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} autoFocus />
            </label>
            <div className="cbo-dialog__btns">
              <button type="button" className="kh-btn" onClick={() => setOverride(null)}>
                {override.kind === "total" ? t("Back") : t("Leave for someone else")}
              </button>
              <button
                type="button"
                className="kh-btn is-primary"
                disabled={!reason.trim() || !!busy}
                onClick={() => {
                  if (override.kind === "total") saveTotal({ reason: reason.trim() });
                  else {
                    verify([override.entry], { reason: reason.trim() }, override.entry.id);
                    setOverride(null);
                  }
                }}
              >
                {override.kind === "total" ? t("Save reason") : t("Verify anyway")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

/** /karkhana/money/cash-book/spends/:photoId — check a supervisor's spends page (11.6 frame 8, 7.8). */
const SpendsCheck = () => (
  <CashbookGuard staffOnly>
    <SpendsScreen />
  </CashbookGuard>
);

export default SpendsCheck;
